import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import posthogAnalytics from '../utils/posthogAnalytics';
import { resolveFeatureName } from './featureTracking';

/**
 * Emits a `feature_used` PostHog event when the user navigates into a feature
 * area of the app. Coarse by design: it fires on the top-level section, deduping
 * consecutive repeats so moving *within* a feature (e.g. an account detail page)
 * doesn't re-fire. No-op until `enabled` (typically `isAuthenticated`) is true.
 *
 * @param {boolean} enabled Whether tracking is active (gate on auth).
 */
export function useFeatureTracking(enabled) {
  const location = useLocation();
  const lastFeatureRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      // Clear on logout so the next session re-tracks even the same landing feature.
      lastFeatureRef.current = null;
      return;
    }

    const feature = resolveFeatureName(location.pathname);
    if (!feature || feature === lastFeatureRef.current) return;

    lastFeatureRef.current = feature;
    posthogAnalytics.trackFeatureUsed(feature, { path: location.pathname });
  }, [enabled, location.pathname]);
}

export default useFeatureTracking;
